import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { slidingEmptySlotBackground } from "../../../admin/story/slidingEmptySlotStyle";
import { useConfetti } from "../../../context/ConfettiContext";
import { usePagesTranslation } from "../../locales";

const GRID_GAP_PX = 2;
const PRAISE_VISIBLE_MS = 1400;
const COMPLETE_CONFETTI_MS = 5000;
/** After this many guided clicks, hide “Click” labels; play continues until solved. */
const MAX_GUIDED_CLICKS = 2;

function normalizeLayout(startingLayout, n) {
    if (!Array.isArray(startingLayout) || startingLayout.length !== n || n <= 0) return [];
    return startingLayout.map((x) => Number(x));
}

function isIdentityLayout(layout) {
    return Array.isArray(layout) && layout.length > 0 && layout.every((v, i) => Number(v) === i);
}

function cellsAdjacent(gs, indexA, indexB) {
    if (indexA < 0 || indexB < 0) return false;
    const ra = Math.floor(indexA / gs);
    const ca = indexA % gs;
    const rb = Math.floor(indexB / gs);
    const cb = indexB % gs;
    return Math.abs(ra - rb) + Math.abs(ca - cb) === 1;
}

/**
 * Standalone how-to board (not the play page). Neighbors of the empty cell show “Click”
 * for the first two moves (with praise). Play continues until the board is solved —
 * then tiles snap to place, full image + confetti.
 */
export default function SlidingHowToMoveExample({ imageUrl, gridSize, startingLayout }) {
    const { t } = usePagesTranslation();
    const { triggerConfetti } = useConfetti();
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : 0;
    const emptyIdx = n > 0 ? n - 1 : -1;

    const initialLayout = useMemo(
        () => normalizeLayout(startingLayout, n),
        [startingLayout, n],
    );
    const [layout, setLayout] = useState(initialLayout);
    const [guidedClicks, setGuidedClicks] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [praise, setPraise] = useState(null);
    const praiseTimerRef = useRef(null);
    const confettiFiredRef = useRef(false);

    useEffect(() => {
        setLayout(initialLayout);
        setGuidedClicks(0);
        setCompleted(false);
        setPraise(null);
        confettiFiredRef.current = false;
    }, [initialLayout]);

    useEffect(
        () => () => {
            if (praiseTimerRef.current) clearTimeout(praiseTimerRef.current);
        },
        [],
    );

    useEffect(() => {
        if (!completed || confettiFiredRef.current) return;
        confettiFiredRef.current = true;
        triggerConfetti(COMPLETE_CONFETTI_MS);
    }, [completed, triggerConfetti]);

    const emptyVisualPos = layout.findIndex((v) => Number(v) === emptyIdx);
    const showClickHints = !completed && guidedClicks < MAX_GUIDED_CLICKS;

    const showPraiseBanner = (text) => {
        if (praiseTimerRef.current) clearTimeout(praiseTimerRef.current);
        setPraise({ text, id: Date.now() });
        praiseTimerRef.current = setTimeout(() => {
            setPraise(null);
            praiseTimerRef.current = null;
        }, PRAISE_VISIBLE_MS);
    };

    const completePuzzle = () => {
        const solved = Array.from({ length: n }, (_, i) => i);
        setLayout(solved);
        setCompleted(true);
        setGuidedClicks(MAX_GUIDED_CLICKS);
        showPraiseBanner(t("puzzle.sliding.how-to-move-praise-2", "Great !!"));
    };

    const handleClickCell = (visualCellIdx) => {
        if (completed) return;
        if (emptyVisualPos < 0) return;
        if (!cellsAdjacent(gs, visualCellIdx, emptyVisualPos)) return;

        const next = [...layout];
        const tmp = next[visualCellIdx];
        next[visualCellIdx] = next[emptyVisualPos];
        next[emptyVisualPos] = tmp;
        setLayout(next);

        if (isIdentityLayout(next)) {
            completePuzzle();
            return;
        }

        if (guidedClicks === 0) {
            showPraiseBanner(t("puzzle.sliding.how-to-move-praise-1", "Good Job"));
            setGuidedClicks(1);
        } else if (guidedClicks === 1) {
            showPraiseBanner(t("puzzle.sliding.how-to-move-praise-2", "Great !!"));
            setGuidedClicks(2);
        }
    };

    if (!imageUrl || !Number.isFinite(gs) || gs < 1 || layout.length !== n) {
        return null;
    }

    const clickLabel = t("puzzle.sliding.how-to-move-click", "Click");

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "1 / 1",
                "@keyframes howToPraiseInOut": {
                    "0%": { opacity: 0, transform: "translate(-50%, -50%) scale(0.72)" },
                    "18%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.08)" },
                    "32%": { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
                    "75%": { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
                    "100%": { opacity: 0, transform: "translate(-50%, -50%) scale(0.94)" },
                },
                "@keyframes howToClickPulse": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.18)" },
                },
                "@keyframes howToRevealImage": {
                    "0%": { opacity: 0, transform: "scale(1.04)" },
                    "100%": { opacity: 1, transform: "scale(1)" },
                },
            }}
        >
            {completed ? (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 1,
                        overflow: "hidden",
                        border: 1,
                        borderColor: "divider",
                        bgcolor: "grey.900",
                        animation: "howToRevealImage 0.55s ease-out both",
                    }}
                >
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={t(
                            "puzzle.sliding.how-to-move-complete-alt",
                            "Completed example — full puzzle image",
                        )}
                        referrerPolicy="no-referrer"
                        sx={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "grid",
                        gridTemplateColumns: `repeat(${gs}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${gs}, minmax(0, 1fr))`,
                        gap: `${GRID_GAP_PX}px`,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "action.hover",
                    }}
                    aria-label={t(
                        "puzzle.sliding.how-to-move-example-aria",
                        "Interactive example — tap Click on a tile next to the empty cell",
                    )}
                >
                    {layout.map((sliceIndexAtCell, visualCellIdx) => {
                        const sliceIndex = Number(sliceIndexAtCell);
                        const isEmpty = sliceIndex === emptyIdx;
                        const col = sliceIndex % gs;
                        const row = Math.floor(sliceIndex / gs);
                        const isNeighbor =
                            !isEmpty &&
                            emptyVisualPos >= 0 &&
                            cellsAdjacent(gs, visualCellIdx, emptyVisualPos);
                        const canClick = isNeighbor;
                        const showClick = canClick && showClickHints;

                        return (
                            <Box
                                key={`howto-cell-${visualCellIdx}`}
                                component={canClick ? "button" : "div"}
                                type={canClick ? "button" : undefined}
                                onClick={canClick ? () => handleClickCell(visualCellIdx) : undefined}
                                aria-label={showClick ? clickLabel : undefined}
                                sx={{
                                    position: "relative",
                                    minWidth: 0,
                                    minHeight: 0,
                                    width: "100%",
                                    height: "100%",
                                    p: 0,
                                    m: 0,
                                    border: 0,
                                    borderRadius: "2px",
                                    overflow: "hidden",
                                    display: "block",
                                    cursor: canClick ? "pointer" : "default",
                                    appearance: "none",
                                    bgcolor: "transparent",
                                    WebkitTapHighlightColor: "transparent",
                                    outline: "none",
                                    ...(canClick
                                        ? {
                                              "&:hover": {
                                                  outline: "3px solid #7FB85A",
                                                  outlineOffset: 0,
                                                  zIndex: 2,
                                              },
                                              "&:focus-visible": {
                                                  outline: (theme) =>
                                                      `3px solid ${theme.palette.primary.main}`,
                                                  outlineOffset: 0,
                                                  zIndex: 2,
                                              },
                                          }
                                        : {}),
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "100%",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt=""
                                        draggable={false}
                                        referrerPolicy="no-referrer"
                                        sx={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            width: `${gs * 100}%`,
                                            height: `${gs * 100}%`,
                                            maxWidth: "none",
                                            objectFit: "fill",
                                            transform: `translate(-${(col * 100) / gs}%, -${(row * 100) / gs}%)`,
                                            transformOrigin: "top left",
                                            userSelect: "none",
                                            pointerEvents: "none",
                                        }}
                                    />
                                    {isEmpty ? (
                                        <Box
                                            sx={(theme) => ({
                                                position: "absolute",
                                                inset: 0,
                                                backgroundColor: slidingEmptySlotBackground(theme),
                                                pointerEvents: "none",
                                            })}
                                        />
                                    ) : null}
                                    {showClick ? (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                pointerEvents: "none",
                                                background:
                                                    "radial-gradient(circle at center, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.12) 55%, transparent 75%)",
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    px: 1,
                                                    py: 0.35,
                                                    minHeight: 28,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: 999,
                                                    bgcolor: "rgba(255,255,255,0.94)",
                                                    color: "#1B4332",
                                                    fontWeight: 800,
                                                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                                    letterSpacing: "0.04em",
                                                    textTransform: "uppercase",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                                    border: "1px solid rgba(127, 184, 90, 0.55)",
                                                    transformOrigin: "center",
                                                    animation:
                                                        "howToClickPulse 1.15s ease-in-out infinite",
                                                    willChange: "transform",
                                                }}
                                            >
                                                {clickLabel}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {praise ? (
                <Typography
                    key={praise.id}
                    role="status"
                    aria-live="polite"
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        zIndex: 5,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.95)",
                        color: "#1B4332",
                        fontWeight: 800,
                        fontSize: { xs: "1.15rem", sm: "1.35rem" },
                        letterSpacing: "0.02em",
                        boxShadow: "0 8px 28px rgba(40, 70, 55, 0.28)",
                        border: "2px solid rgba(127, 184, 90, 0.65)",
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                        animation: `howToPraiseInOut ${PRAISE_VISIBLE_MS}ms ease-out both`,
                    }}
                >
                    {praise.text}
                </Typography>
            ) : null}
        </Box>
    );
}

SlidingHowToMoveExample.propTypes = {
    imageUrl: PropTypes.string.isRequired,
    gridSize: PropTypes.number.isRequired,
    startingLayout: PropTypes.arrayOf(PropTypes.number).isRequired,
};
